import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  LayoutDashboard,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';

interface MondayBoard {
  id: string;
  name: string;
  description?: string;
  state?: string;
  url?: string;
  workspace_id?: string | number | null;
  updated_at?: string;
}

interface MondayImportModalProps {
  onClose: () => void;
}

const MondayImportModal: React.FC<MondayImportModalProps> = ({ onClose }) => {
  const [selectedBoards, setSelectedBoards] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'boards' | 'all'>('boards');
  const queryClient = useQueryClient();

  const { data: boardsData, isLoading: boardsLoading } = useQuery({
    queryKey: ['monday-boards'],
    queryFn: async () => {
      const response = await apiClient.get('/api/integrations/monday/boards');
      return (response as any).data ?? response;
    },
    retry: 1,
  });

  const importMutation = useMutation({
    mutationFn: async (data: { board_ids?: string[]; import_all?: boolean }) => {
      const response = await apiClient.customRequest('/api/integrations/monday/import', {
        method: 'POST',
        body: data,
      });
      return response;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      const message =
        data?.total_items !== undefined
          ? `Successfully imported ${data.total_items} items from ${data.total_boards} board(s)!`
          : 'Monday data imported successfully!';
      alert(message);
      onClose();
    },
    onError: (error: any) => {
      alert(`Failed to import Monday data: ${error.message}`);
    },
  });

  const boards: MondayBoard[] = Array.isArray(boardsData) ? boardsData : [];

  const handleBoardToggle = (boardId: string) => {
    const next = new Set(selectedBoards);
    if (next.has(boardId)) {
      next.delete(boardId);
    } else {
      next.add(boardId);
    }
    setSelectedBoards(next);
  };

  const handleSelectAll = () => {
    if (selectedBoards.size === boards.length) {
      setSelectedBoards(new Set());
    } else {
      setSelectedBoards(new Set(boards.map((b) => b.id)));
    }
  };

  const handleImport = () => {
    if (activeTab === 'all') {
      importMutation.mutate({ import_all: true });
    } else if (selectedBoards.size > 0) {
      importMutation.mutate({ board_ids: Array.from(selectedBoards) });
    } else {
      alert('Please select at least one board to import');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 via-green-500 to-blue-500 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Monday Data</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Select boards to import items from</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          <button
            onClick={() => setActiveTab('boards')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'boards'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              <span>By Board ({boards.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>Import All Boards</span>
            </div>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'boards' ? (
            <div>
              <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedBoards.size === boards.length && boards.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-orange-500 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select All Boards</span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedBoards.size} of {boards.length} selected
                </span>
              </div>

              {boardsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading boards...</p>
                  </div>
                </div>
              ) : boards.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No Boards Found</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">No boards available in your monday account</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {boards.map((board) => (
                    <label
                      key={board.id}
                      className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBoards.has(board.id)}
                        onChange={() => handleBoardToggle(board.id)}
                        className="w-4 h-4 text-orange-500 rounded border-gray-300 dark:border-gray-600 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{board.name}</h4>
                        </div>
                        {board.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{board.description}</p>
                        )}
                        {board.updated_at && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Updated: {new Date(board.updated_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-8">
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Import All Monday Boards</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This will import all items (and recent updates) from all accessible boards.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <LayoutDashboard className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">All Boards</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{boards.length} available</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Items</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Including updates</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Date Range</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Full history</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Auto Sync</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Manual re-import</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'boards'
              ? `${selectedBoards.size} board(s) selected`
              : 'Importing all boards'}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={importMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || (activeTab === 'boards' && selectedBoards.size === 0)}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import Data'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MondayImportModal;

